import type { CartItem } from '~/composables/types/cart'

const ITEM_LIST_ATTRIBUTION_STORAGE_KEY = 'konfetti-gtm-item-list-attribution'
export const CHECKOUT_ITEM_LIST_ATTRIBUTION_STORAGE_KEY =
  'checkout-item-list-attribution'

// Item-scoped, last click wins: a later click on the same item from any list
// replaces the whole entry (including `item_search_term`). GA4 has no per-query
// join like Algolia's queryID, so search attribution is per item + term.
export interface ItemListAttribution {
  item_list_id: string
  item_list_name: string
  item_search_term?: string
}

export type ItemListAttributionExtras = Partial<
  Omit<ItemListAttribution, 'item_list_id' | 'item_list_name'>
>

export type ItemListAttributionMap = Record<string, ItemListAttribution>

const normalizeItemListAttributionKey = (
  id: string | number | undefined | null
): string | null => {
  if (id === undefined || id === null || id === '') return null
  return String(id)
}

const readJsonMap = <T extends Record<string, unknown>>(
  storage: Storage | undefined,
  key: string
): T => {
  if (!storage) return {} as T
  try {
    return JSON.parse(storage.getItem(key) || '{}') as T
  } catch {
    return {} as T
  }
}

const writeJsonMap = (
  storage: Storage | undefined,
  key: string,
  map: Record<string, unknown>
): void => {
  if (!storage || Object.keys(map).length === 0) return
  storage.setItem(key, JSON.stringify(map))
}

export const getItemListAttributionMap = (): ItemListAttributionMap => {
  if (typeof localStorage === 'undefined') return {}
  return readJsonMap<ItemListAttributionMap>(
    localStorage,
    ITEM_LIST_ATTRIBUTION_STORAGE_KEY
  )
}

export const getCheckoutItemListAttributionMap = (): ItemListAttributionMap => {
  if (typeof sessionStorage === 'undefined') return {}
  return readJsonMap<ItemListAttributionMap>(
    sessionStorage,
    CHECKOUT_ITEM_LIST_ATTRIBUTION_STORAGE_KEY
  )
}

export const persistItemListAttribution = (
  itemId: string | number,
  attribution: ItemListAttribution
): void => {
  const key = normalizeItemListAttributionKey(itemId)
  if (!key || typeof localStorage === 'undefined') return

  const map = getItemListAttributionMap()
  map[key] = attribution
  localStorage.setItem(ITEM_LIST_ATTRIBUTION_STORAGE_KEY, JSON.stringify(map))
}

export const persistCheckoutGtmItemListMapsToSessionStorage = (
  items: CartItem[] | undefined
): void => {
  if (typeof sessionStorage === 'undefined' || !items?.length) return

  const attributionMap = getItemListAttributionMap()
  const checkoutMap: ItemListAttributionMap = {}

  items.forEach((item) => {
    const key = normalizeItemListAttributionKey(item.eventDescriptionId)
    if (!key) return
    const attribution = attributionMap[key]
    if (attribution) {
      checkoutMap[key] = attribution
    }
  })

  writeJsonMap(
    sessionStorage,
    CHECKOUT_ITEM_LIST_ATTRIBUTION_STORAGE_KEY,
    checkoutMap
  )
}

export const clearCheckoutItemListAttributionFromSessionStorage = (): void => {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(CHECKOUT_ITEM_LIST_ATTRIBUTION_STORAGE_KEY)
}

// Every enriched item carries `item_search_term` ('' when the item wasn't clicked
// from a search) so a GA4 item dimension never inherits a stale term.
export const enrichGtmItemsWithListAttribution = <
  T extends { item_id: string | number },
>(
  items: T[],
  attributionMap?: ItemListAttributionMap
): T[] => {
  const map = attributionMap ?? getItemListAttributionMap()

  return items.map((item) => {
    const key = normalizeItemListAttributionKey(item.item_id)
    const attribution = key ? map[key] : undefined
    if (!attribution) return { ...item, item_search_term: '' }
    return {
      ...item,
      item_list_id: attribution.item_list_id,
      item_list_name: attribution.item_list_name,
      item_search_term: attribution.item_search_term ?? '',
    }
  })
}
